import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllContactEnquiriesThunk,
  deleteContactEnquiryThunk,
} from "../../redux/states/conactus/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Box, Delete, Edit, Eye, Plus, Trash } from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";

const ContactUs = () => {
  const dispatch = useDispatch();
  const { contactUs, loading, error } = useSelector((state) => state.contactUs);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getAllContactEnquiriesThunk());
  }, [dispatch]);

  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(deleteContactEnquiryThunk(selectedId));
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-conactus/${uuid}`);
  };

  const handleView = (uuid) => {
    navigate(`/view-conactus/${uuid}`);
  };

  const handleCreate = () => {
    navigate("/add-new-conactus");
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-3">
        {/* Header Section */}
        <div className="mb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                Contact Us
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                Manage and review all conact us Querys
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="mt-3 md:mt-0 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <Plus className="mr-2" size={18} /> New Contact Us
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 text-lg">Loading enquiries...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-red-100 p-2 rounded-lg mr-4">
                  <Trash className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-red-800 font-semibold text-lg">Error</h3>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="text-red-600 hover:text-red-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Table View */}
        {!loading && contactUs && contactUs.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-gray-100 text-gray-700 text-xs uppercase">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">#</th>
                  <th className="py-3 px-4 text-left font-semibold">Name</th>
                  <th className="py-3 px-4 text-left font-semibold">Email</th>
                  <th className="py-3 px-4 text-left font-semibold">Phone</th>
                  <th className="py-3 px-4 text-left font-semibold">Status</th>
                  <th className="py-3 px-4 text-left font-semibold">
                    Created At
                  </th>
                  <th className="py-3 px-4 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {contactUs.map((item, index) => (
                  <tr
                    key={item.uuid}
                    className="border-t hover:bg-gray-50 transition-all duration-200"
                  >
                    <td className="py-3 px-4 text-gray-600">{index + 1}</td>
                    <td className="py-3 px-4 font-semibold text-gray-800">
                      {item.name || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {item.email || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {item.phone || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {item.created_at || "—"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleView(item.uuid)}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(item.uuid)}
                          className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 p-2 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item.uuid)}
                          className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition"
                          title="Delete"
                        >
                          <Delete size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="bg-gray-100 p-8 rounded-2xl mb-6">
                <Box className="text-6xl text-gray-400 mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-600 mb-3">
                No Enquiries Found
              </h3>
              <button
                onClick={handleCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center transition-all duration-300 transform hover:scale-105"
              >
                <Plus className="mr-2" />
                Add Your First Enquiry
              </button>
            </div>
          )
        )}

        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Enquiry"
          message="Do you really want to delete this enquiry? This action cannot be undone."
        />
      </div>
    </NonAuthLayout>
  );
};

export default ContactUs;
